/**
 * File Validation Utilities
 *
 * Validates file types and sizes according to the upload portal allowlist.
 */

// Allowed MIME types
export const ALLOWED_MIME_TYPES = {
  image: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/heic'],
  video: ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/webm'],
  audio: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-m4a', 'audio/aac'],
  text: ['text/plain', 'text/markdown'],
};

// Allowed file extensions (backup check)
export const ALLOWED_EXTENSIONS = {
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic'],
  video: ['.mp4', '.mov', '.m4v', '.webm'],
  audio: ['.mp3', '.wav', '.m4a', '.aac'],
  text: ['.txt', '.md'],
};

// Flatten all allowed extensions
export const ALL_ALLOWED_EXTENSIONS = [
  ...ALLOWED_EXTENSIONS.image,
  ...ALLOWED_EXTENSIONS.video,
  ...ALLOWED_EXTENSIONS.audio,
  ...ALLOWED_EXTENSIONS.text,
];

export type FileTypeCategory = 'image' | 'video' | 'audio' | 'text' | 'unknown';

export interface FileValidationResult {
  valid: boolean;
  error?: string;
  category?: FileTypeCategory;
}

/**
 * Get file extension from filename
 * @param filename - The filename to extract extension from
 * @returns The file extension (including dot) in lowercase, or empty string if no extension
 */
function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return '';
  return filename.substring(lastDot).toLowerCase();
}

/**
 * Determine file type category from MIME type or extension
 * @param file - The file to categorize
 * @returns The file type category (image, video, audio, text, or unknown)
 */
export function getFileTypeCategory(file: File): FileTypeCategory {
  const extension = getFileExtension(file.name);
  const mimeType = file.type.toLowerCase();

  // Check MIME type first
  for (const [category, mimes] of Object.entries(ALLOWED_MIME_TYPES)) {
    if (mimes.some(mime => mimeType.includes(mime.split('/')[1]))) {
      return category as FileTypeCategory;
    }
  }

  // Fallback to extension check
  for (const [category, extensions] of Object.entries(ALLOWED_EXTENSIONS)) {
    if (extensions.includes(extension)) {
      return category as FileTypeCategory;
    }
  }

  return 'unknown';
}

/**
 * Validate file type against allowlist
 * @param file - The file to validate
 * @returns Validation result with valid flag and optional error message
 */
export function validateFileType(file: File): FileValidationResult {
  const extension = getFileExtension(file.name);
  const mimeType = file.type.toLowerCase();
  const category = getFileTypeCategory(file);

  // Check if extension is allowed
  const extensionAllowed = ALL_ALLOWED_EXTENSIONS.includes(extension);

  // Check if MIME type matches an allowed pattern
  let mimeAllowed = false;
  if (mimeType) {
    for (const mimes of Object.values(ALLOWED_MIME_TYPES)) {
      if (
        mimes.some(
          allowedMime => mimeType === allowedMime || mimeType.includes(allowedMime.split('/')[1])
        )
      ) {
        mimeAllowed = true;
        break;
      }
    }
  }

  // If no MIME type is provided, rely on extension
  if (!mimeType && extensionAllowed) {
    mimeAllowed = true;
  }

  if (!extensionAllowed && !mimeAllowed) {
    return {
      valid: false,
      error: `File type not allowed. Allowed types: images (${ALLOWED_EXTENSIONS.image.join(', ')}), videos (${ALLOWED_EXTENSIONS.video.join(', ')}), audio (${ALLOWED_EXTENSIONS.audio.join(', ')}), text (${ALLOWED_EXTENSIONS.text.join(', ')})`,
      category: 'unknown',
    };
  }

  if (category === 'unknown') {
    return {
      valid: false,
      error: 'Could not determine file type. Please ensure file has a valid extension.',
      category: 'unknown',
    };
  }

  return {
    valid: true,
    category,
  };
}

/**
 * Validate file size against maximum
 * @param file - The file to validate
 * @param maxFileBytes - Maximum allowed file size in bytes (optional)
 * @returns Validation result with valid flag and optional error message
 */
export function validateFileSize(file: File, maxFileBytes?: number): FileValidationResult {
  if (!maxFileBytes) {
    return { valid: true };
  }

  if (file.size > maxFileBytes) {
    const maxGB = (maxFileBytes / (1024 * 1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `File size (${(file.size / (1024 * 1024 * 1024)).toFixed(2)} GB) exceeds maximum allowed size (${maxGB} GB)`,
    };
  }

  return { valid: true };
}

/**
 * Validate file against both type and size constraints
 * @param file - The file to validate
 * @param maxFileBytes - Maximum allowed file size in bytes (optional)
 * @returns Validation result with valid flag, category, and optional error message
 */
export function validateFile(file: File, maxFileBytes?: number): FileValidationResult {
  const typeValidation = validateFileType(file);
  if (!typeValidation.valid) {
    return typeValidation;
  }

  const sizeValidation = validateFileSize(file, maxFileBytes);
  if (!sizeValidation.valid) {
    return sizeValidation;
  }

  return {
    valid: true,
    category: typeValidation.category,
  };
}
